import { useEffect, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import PipelineDealCard from './PipelineDealCard.jsx';
import DraggableCard from './DraggableCard.jsx';
import EmptyColumn from './EmptyColumn.jsx';
import { cn } from '@/lib/utils.js';
import { toDate } from '@/lib/date-display.js';

const COLLAPSE_KEY = (stage) => `pipeline:collapsed:${stage}`;

// Per spec §3.4:
//   - Header: stage name + count
//   - Header tap toggles collapse; persists per-broker in localStorage
//   - Sort dropdown (urgency / name / activity) — sortBy prop from parent
//   - Empty state: one-line muted message
//   - Drop target — column highlights when card is dragged over it

export default function StageColumn({ stage, items, sortBy = 'urgency', onContextMenu, onChange }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_KEY(stage)) === '1';
  });
  const { setNodeRef, isOver } = useDroppable({ id: stage });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(COLLAPSE_KEY(stage), collapsed ? '1' : '0');
  }, [stage, collapsed]);

  const sorted = sortItems(items, sortBy);

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'flex w-72 shrink-0 flex-col gap-2 rounded-card p-1 transition-colors',
        isOver ? 'bg-accent-soft ring-1 ring-accent/40' : 'bg-transparent'
      )}
    >
      <header className="sticky top-0 z-10 -mx-1 flex items-center justify-between bg-bg/95 px-2 py-2 backdrop-blur">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-1.5 text-left"
        >
          {collapsed ? (
            <ChevronRight size={14} strokeWidth={1.5} className="text-text-dim" />
          ) : (
            <ChevronDown size={14} strokeWidth={1.5} className="text-text-dim" />
          )}
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">
            {stage}
          </h2>
          <span className="font-mono text-[10px] text-text-dim">({items.length})</span>
        </button>
      </header>
      {!collapsed ? (
        items.length === 0 ? (
          <EmptyColumn />
        ) : (
          <div className="flex flex-col gap-2">
            {sorted.map((item) => (
              <DraggableCard key={item.deal_id} id={String(item.deal_id)}>
                <PipelineDealCard item={item} onContextMenu={onContextMenu} onChange={onChange} />
              </DraggableCard>
            ))}
          </div>
        )
      ) : null}
    </section>
  );
}

const URGENCY_RANK = {
  CRITICAL: 0,
  EXPIRED: 0,
  URGENT: 1,
  NORMAL: 2,
  COMFORTABLE: 3,
};

function sortItems(items, sortBy) {
  const list = [...items];
  if (sortBy === 'name') {
    list.sort((a, b) =>
      String(a.named_insured || '').localeCompare(String(b.named_insured || ''))
    );
  } else if (sortBy === 'activity') {
    list.sort(
      (a, b) =>
        (toDate(b.stage_changed_at)?.getTime() ?? 0) -
        (toDate(a.stage_changed_at)?.getTime() ?? 0)
    );
  } else {
    list.sort((a, b) => {
      const ra = URGENCY_RANK[a.urgency] ?? 9;
      const rb = URGENCY_RANK[b.urgency] ?? 9;
      if (ra !== rb) return ra - rb;
      return (a.days_until_expiration ?? 9999) - (b.days_until_expiration ?? 9999);
    });
  }
  return list;
}
